import React, { createContext } from "react";
import {
  DeviceAdapter,
  DeviceModelDetail,
  SimpleEntry,
  SnmpEntry,
} from "@services/catalog/types";

export enum DeviceModelTab {
  NONE = -1,
  SNMP = 0,
  ADAPTERS = 1,
  MAPPING = 2,
}
type Errors = { mapping: boolean; oid: boolean };
export type ReducerState = {
  accountId?: string;
  device: DeviceModelDetail;
  globalDevice: DeviceModelDetail;
  deviceAdapters: DeviceAdapter[];
  snmpEnabled: boolean;
  snmpEntries: SnmpEntry[];
  snmpInherit: boolean;
  adapterInherit: boolean;
  mappingInherit: boolean;
  localMode: boolean;
  adapterChecked: boolean;
  adapterSelected: DeviceAdapter["id"];
  propertyMappingEnabled: boolean;
  mappingEntries: SimpleEntry[];
  tabSelected: DeviceModelTab;
  errors: Errors;
  canEdit: boolean;
  canAccessExtendedPropertyMapping: boolean;
  isDataChanged: boolean;
};
export const initialState: ReducerState = {
  device: {},
  globalDevice: {},
  deviceAdapters: [],
  snmpEnabled: false,
  snmpEntries: [],
  snmpInherit: true,
  adapterInherit: true,
  mappingInherit: true,
  localMode: false,
  adapterChecked: false,
  adapterSelected: undefined,
  propertyMappingEnabled: false,
  mappingEntries: [],
  tabSelected: DeviceModelTab.NONE,
  errors: { mapping: false, oid: false },
  canEdit: false,
  canAccessExtendedPropertyMapping: false,
  isDataChanged: false,
};

export const ReducerContext = createContext<ReducerState>(initialState);

type WithAction<T extends string, D> = { type: T; value?: D };

export type ReducerActions =
  | Partial<ReducerState>
  | WithAction<"openDialog", boolean>
  | WithAction<"addSnmpEntry", any>
  | WithAction<"removeSnmpEntry", (SnmpEntry | undefined)[] | undefined>
  | WithAction<"addMappingEntry", any>
  | WithAction<"removeMappingEntry", (SimpleEntry | undefined)[] | undefined>
  | WithAction<"toggleSNMPInherit", any>
  | WithAction<"toggleLocalMode", any>
  | WithAction<"toggleMappingInherit", any>
  | WithAction<"setErrors", any>
  | WithAction<"closeDialog", any>;

export const ReducerDispatchContext = createContext<
  React.Dispatch<ReducerActions>
>(() => initialState);

export const isWithAction = (x: ReducerActions): x is WithAction<any, any> =>
  (x as WithAction<any, any>).type !== undefined;

export const stateReducer = (
  state: ReducerState,
  action: ReducerActions
): ReducerState => {
  if (isWithAction(action)) {
    switch (action.type) {
      case "openDialog":
        const inheritedSNMP =
          Boolean(state.accountId) &&
          !(
            state.device.snmpEnabled != state.globalDevice.snmpEnabled ||
            state.device.overrideGlobalOids
          );

        const canAccessExtendedPropertyMapping =
          Boolean(state.accountId) && state.canAccessExtendedPropertyMapping;

        const inheritedMapping = canAccessExtendedPropertyMapping
          ? !(
              state?.device?.propertyMappingEnabled !=
                state?.globalDevice?.propertyMappingEnabled ||
              state?.device.overrideGlobalPropertyMapping
            ) && Boolean(state.accountId)
          : !Boolean(state.accountId);

        const mappingEntries =
          Object.entries(
            !inheritedMapping
              ? state?.device?.mappedExtendedProperties ?? {}
              : state?.globalDevice?.mappedExtendedProperties ?? {}
          ).map((e) => {
            return { name: e[0], value: e[1] };
          }) ?? [];
        const adapterSelected =
          state.device?.defaultDeviceAdapter?.id ===
          state.globalDevice?.defaultDeviceAdapter?.id
            ? state.globalDevice?.defaultDeviceAdapter?.id
            : state.device?.defaultDeviceAdapter?.id;

        const snmpEntries = state.device.overrideGlobalOids
          ? state.device.snmpEntries ?? []
          : state.globalDevice.snmpEntries ?? [];

        const propertyMappingEnabled =
          (state.device.propertyMappingEnabled ?? false) ||
          Object.entries(state.device.mappedExtendedProperties ?? {}).length >
            0;
        const tabSelected =
          state.device.snmpEnabled ?? false
            ? DeviceModelTab.SNMP
            : (state.device.totalAdapters ?? 0) > 0 &&
              Boolean(state.device.defaultDeviceAdapter)
            ? DeviceModelTab.ADAPTERS
            : state.device.propertyMappingEnabled &&
              canAccessExtendedPropertyMapping
            ? DeviceModelTab.MAPPING
            : DeviceModelTab.NONE;

        return {
          ...state,
          canEdit: action.value ?? false,
          snmpEnabled: state.device.snmpEnabled ?? false,
          adapterInherit:
            state.device?.defaultDeviceAdapter?.id ===
            state.globalDevice?.defaultDeviceAdapter?.id,
          adapterChecked: Boolean(state.device.defaultDeviceAdapter),
          snmpInherit: inheritedSNMP,
          snmpEntries,
          localMode: state.device.overrideGlobalOids ?? false,
          adapterSelected,
          propertyMappingEnabled,
          mappingEntries,
          mappingInherit: inheritedMapping,
          tabSelected,
          canAccessExtendedPropertyMapping,
        };

      case "addSnmpEntry":
        if (state.snmpEntries !== undefined) {
          return {
            ...state,
            isDataChanged: true,
            snmpEntries: [...state.snmpEntries, { name: "", oid: "" }].sort(
              (a, b) => a.name?.localeCompare(b?.name ?? "") ?? 0
            ),
          };
        } else {
          return {
            ...state,
            isDataChanged: true,
            snmpEntries: [{ name: "", oid: "" }],
          };
        }
      case "removeSnmpEntry":
        const entries = state.snmpEntries?.filter(
          (entry) => !action.value?.includes(entry)
        );

        return {
          ...state,
          isDataChanged: true,
          snmpEntries: entries,
        };
      case "addMappingEntry":
        if (state.mappingEntries !== undefined) {
          return {
            ...state,
            isDataChanged: true,
            mappingEntries: [...state.mappingEntries, { name: "", value: "" }],
          };
        } else {
          return {
            ...state,
            mappingEntries: [{ name: "", value: "" }],
          };
        }
      case "removeMappingEntry":
        const tmpEntries = state.mappingEntries?.filter(
          (entry) => !action.value?.includes(entry)
        );

        return {
          ...state,
          isDataChanged: true,
          mappingEntries: tmpEntries,
        };
      case "toggleLocalMode":
        return {
          ...state,
          isDataChanged: true,
          localMode: !state.localMode,
          snmpEntries: state.localMode
            ? state.globalDevice.snmpEntries ?? []
            : state.snmpEntries,
        };
      case "toggleSNMPInherit":
        return {
          ...state,
          snmpInherit: !state.snmpInherit,
          localMode: state.snmpInherit ? state.localMode : false,
          snmpEnabled: state.snmpInherit
            ? state.device.snmpEnabled ?? false
            : state.globalDevice.snmpEnabled ?? false,
          snmpEntries: state.snmpInherit
            ? state.globalDevice.snmpEntries ?? []
            : state.snmpEntries,
        };
      case "toggleMappingInherit":
        const globalPropertyMappingEnabled =
          (state.globalDevice?.propertyMappingEnabled ?? false) ||
          Object.entries(state.globalDevice.mappedExtendedProperties ?? {})
            .length > 0;

        return {
          ...state,
          mappingInherit: !state.mappingInherit,
          mappingEntries: !state.mappingInherit
            ? Object.entries(
                state?.globalDevice?.mappedExtendedProperties ?? {}
              ).map((e) => {
                return { name: e[0], value: e[1] };
              })
            : state.mappingEntries,
          propertyMappingEnabled: !state.mappingInherit
            ? globalPropertyMappingEnabled
            : state.mappingEntries.length > 0,
        };
      case "closeDialog":
        return {
          ...initialState,
        };
      case "setErrors":
        const errors = state.errors;
        if (
          state.propertyMappingEnabled &&
          state.mappingEntries.some((entries) => {
            return entries.name === "" || entries.value === "";
          })
        ) {
          errors.mapping = true;
        } else {
          errors.mapping = false;
        }

        if (
          state.localMode &&
          state.snmpEntries.some((snmp) => {
            return snmp.name === "" || snmp.oid === "";
          })
        ) {
          errors.oid = true;
        } else {
          errors.oid = false;
        }
        return { ...state, errors: { ...errors } };

      default:
        return state;
    }
  }
  return { ...state, ...action };
};
